import React, { useState, useEffect, useRef } from "react";
import { Paper, Typography, Box, Button, TextField, Select, MenuItem, IconButton, List, ListItem, ListItemText } from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CreateCompanyModal from './CreateCompanyModal';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { getTenantIdAuth0, isAdminAuth0 } from "./authUtils";
// Import new API services
import companyApi from './api/companyApi';
import underwritingApi from './api/underwritingApi';
import tenantApi from './api/tenantApi';
import axios from 'axios';
import { gfm } from 'turndown-plugin-gfm'; 
import TurndownService from 'turndown';
import ReactMarkdown from 'react-markdown';
import ReportViewer from './MarkdownReport';
import MDEditor from '@uiw/react-md-editor';
import { useRolePermissions } from './context/RolePermissionsContext';
import { RestrictedAction, RestrictedRegion } from './components/RestrictedAction';
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import LockClockIcon from '@mui/icons-material/LockClock';
import dayjs from 'dayjs';

const ManualSourceForm = ({
  manualSource,
  handleManualSourceChange,
  loading,
  errors,
  setErrors,
  setManualSource,
  company,
  setLoading,
  validateFields,
  companySummary,
  setCompanySummary,
  readOnly = false,
}) => {
  const [newField, setNewField] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");
  const [customFieldName, setCustomFieldName] = useState("");
  const [customFieldValue, setCustomFieldValue] = useState("");

  const isNumeric = (value) => !isNaN(parseFloat(value)) && isFinite(value);



  const allPossibleFields = [
    "description", "industry", "founders", "keyPeople", "founded",
    "ticker", "website", "country", "city", "parentCompany", "competitors",
    "creditRating", "mainLocationOffice", "equity", "netIncome", "revenue",
    "numberOfEmployees", "marketCap", "yearsOfOperation", "AQR",
  ];

  const numericFields = [
    "assets", "operatingIncome", "revenue", "marketCap", "numberOfEmployees",
    "equity", "netIncome", "yearsOfOperation", "AQR"
  ];

  const predefinedFieldsList = [
    "equity", "netIncome", "revenue", "numberOfEmployees", "marketCap",
    "yearsOfOperation", "AQR"
  ];

  const getFieldValue = (fieldData) => {
    if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
      return fieldData.value ?? '';
    }
    return fieldData ?? '';
  };

  const getFieldSource = (fieldData) => {
    if (fieldData && typeof fieldData === 'object' && fieldData.source !== undefined) {
      return fieldData.source;
    }
    return 'unknown';
  };

  const validateField = (fieldName, value) => {
    let error = null;
    if (numericFields.includes(fieldName)) {
      if (value && !isNumeric(value)) {
        error = `${fieldName} must be a numerical value`;
      }
    }
    return error;
  };

  const manualSourceFields = Object.keys(manualSource);

  const resolvedDataFields = companySummary?.resolvedData || {};

  const otherSourceFields = Object.keys(resolvedDataFields).filter(key => {
    if (getFieldSource(resolvedDataFields[key]) === "manualSource") return false;
    const value = getFieldValue(resolvedDataFields[key]);
    return value !== null && value !== undefined && value !== '';
  });

  // For dropdown add new field: only fields not already in manualSource
  const missingFields = allPossibleFields.filter(field => !(field in manualSource));

  // --- Handlers ---

  const handleResolvedDataFieldChange = (fieldName, value) => {
    const validationError = validateField(fieldName, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: validationError,
    }));

    if (!validationError) {
      const updatedSummary = {
        ...companySummary,
        resolvedData: {
          ...companySummary?.resolvedData,
          [fieldName]: {
            value,
            source: companySummary?.resolvedData?.[fieldName]?.source || 'manualSource'
          }
        }
      };
      setCompanySummary(updatedSummary);
    }
  };

  const handleUpdateFinancialInfo = async () => {
    setLoading(true);
    try {
      if (validateFields()) {
        // Update company manualSource
        const updateData = {
          tenantId: company.tenantId,
          id: company.id,
          manualSource: manualSource,
        };
        await companyApi.updateCompany(company.id, updateData);

        // Merge manualSource fields into resolvedData with source 'manualSource'
        const updatedResolvedData = {
          ...companySummary.resolvedData,
          ...Object.fromEntries(
            Object.entries(manualSource).map(([key, val]) => [key, { value: val, source: 'manualSource' }])
          )
        };

        const updatedCompanySummary = {
          ...companySummary,
          resolvedData: updatedResolvedData,
        };

        await underwritingApi.updateCompanySummary(company.tenantId, company.id, updatedCompanySummary);
        setCompanySummary(updatedCompanySummary);

        toast.success("All information updated successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating information");
    }
    setLoading(false);
  };

  // Add new predefined or possible field
  const handleAddNewField = () => {
    let newErrors = {};
    const validationError = validateField(newField, newFieldValue);
    if (validationError) {
      newErrors[newField] = validationError;
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setManualSource((prevManualSource) => {
        const updatedManualSource = {
          ...prevManualSource,
          [newField]: newFieldValue,
        };

        const updateData = {
          tenantId: company.tenantId,
          id: company.id,
          manualSource: updatedManualSource,
        };

        companyApi.updateCompany(company.id, updateData)
          .then(() => {
            console.log("New field added to company manualSource:", newField);
          })
          .catch((error) => {
            console.error("Error updating company manualSource:", error);
            toast.error("Error updating company data");
          });

        return updatedManualSource;
      });

      const updatedSummary = {
        ...companySummary,
        resolvedData: {
          ...companySummary?.resolvedData,
          ...Object.fromEntries(
             Object.entries({ ...manualSource, [newField]: newFieldValue }).map(([key, val]) => [key, { value: val, source: 'manualSource' }])
           )
        }
      };

      underwritingApi.updateCompanySummary(company.tenantId, company.id, updatedSummary)
        .then(() => {
          setCompanySummary(updatedSummary);
          toast.success(`New field "${newField}" added successfully!`);
        })
        .catch((error) => {
          console.error("Error updating company summary resolvedData:", error);
          toast.error("Error updating company summary data");
        });

      setNewField("");
      setNewFieldValue("");
    }
  };

  // Add custom field
  const handleAddCustomField = () => {
    let newErrors = {};
    if (!customFieldName) {
      newErrors.customFieldName = "Custom field name cannot be empty";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setManualSource((prevManualSource) => {
        const updatedManualSource = {
          ...prevManualSource,
          [customFieldName]: customFieldValue,
        };

        const updateData = {
          tenantId: company.tenantId,
          id: company.id,
          manualSource: updatedManualSource,
        };

        companyApi.updateCompany(company.id, updateData)
          .then(() => {
            console.log("Custom field added to company manualSource:", customFieldName);
          })
          .catch((error) => {
            console.error("Error updating company manualSource:", error);
            toast.error("Error updating company data");
          });

        return updatedManualSource;
      });

      const updatedSummary = {
        ...companySummary,
        resolvedData: {
          ...companySummary?.resolvedData,
           ...Object.fromEntries(
             Object.entries({ ...manualSource, [customFieldName]: customFieldValue }).map(([key, val]) => [key, { value: val, source: 'manualSource' }])
           )
        }
      };

      underwritingApi.updateCompanySummary(company.tenantId, company.id, updatedSummary)
        .then(() => {
          setCompanySummary(updatedSummary);
          toast.success(`Custom field "${customFieldName}" added successfully!`);
        })
        .catch((error) => {
          console.error("Error updating company summary resolvedData:", error);
          toast.error("Error updating company summary data");
        });

      setCustomFieldName("");
      setCustomFieldValue("");
    }
  };

  // Delete field
  const handleDeleteResolvedDataField = (field) => {
    // Remove from manualSource if exists
    if (manualSource.hasOwnProperty(field)) {
      setManualSource((prevManualSource) => {
        const updatedManualSource = { ...prevManualSource };
        delete updatedManualSource[field];
        const updateData = {
          tenantId: company.tenantId,
          id: company.id,
          manualSource: updatedManualSource,
        };

        companyApi.updateCompany(company.id, updateData)
          .then(() => {
            console.log("Field removed from company manualSource:", field);
          })
          .catch((error) => {
            console.error("Error removing field from company manualSource:", error);
            toast.error("Error removing field from company data");
          });

        return updatedManualSource;
      });
    }

    underwritingApi.removeResolvedDataFields(company.tenantId, company.id, [field])
      .then(() => {
        const updatedResolvedData = { ...companySummary?.resolvedData };
        delete updatedResolvedData[field];
        setCompanySummary({
          ...companySummary,
          resolvedData: updatedResolvedData
        });
        toast.success(`Field "${field}" deleted successfully!`);
      })
      .catch((error) => {
        console.error("Error removing field from company summary resolvedData:", error);
        toast.error("Error removing field from company summary data");
      });
  };

  return (
    <div>
      <Typography variant='h6' fontWeight='bold' sx={{ alignSelf: "flex-start", mb: 2 }}>
        Company Resolved Data
      </Typography>

      {/* Manual Source Fields */}
      {manualSourceFields.length > 0 && (
        <>
          <Typography variant='subtitle1' fontWeight='bold' color='primary'
            sx={{ alignSelf: "flex-start", mb: 1, mt: 2 }}>
            Manual Source Fields
          </Typography>
          {manualSourceFields.map(key => (
            <div style={{ display: "flex", alignItems: "center" }} key={key}>
              <TextField
                label={`${key} (Manual Source)`}
                fullWidth
                error={!!errors[key]}
                helperText={errors[key]}
                value={manualSource[key] ?? ''}
                onChange={e => {
                  const val = e.target.value;
                  const error = validateField(key, val);
                  setErrors(prev => ({ ...prev, [key]: error }));
                  if (!error) {
                    handleManualSourceChange(key, val);
                  }
                }}
                disabled={readOnly}
                sx={{ mb: 2 }}
              />
              <RestrictedAction allowed={!readOnly}>
              <IconButton
                onClick={() => handleDeleteResolvedDataField(key)}
                sx={{ marginLeft: 1, marginBottom: 2 }}
                color="error"
                aria-label="Delete"
              >
                <CancelIcon />
              </IconButton>
              </RestrictedAction>
            </div>
          ))}
        </>
      )}

      {/* Other Resolved Data Fields */}
      {otherSourceFields.length > 0 && (
        <>
          <Typography variant='subtitle1' fontWeight='bold' sx={{ alignSelf: "flex-start", mb: 1, mt: 2 }}>
            Other Source Fields
          </Typography>
          {otherSourceFields.map(key => {
            const value = resolvedDataFields[key];
            const source = getFieldSource(value);
            const isManual = source === "manualSource";
            let displayValue = getFieldValue(value);
            if (["keyPeople", "socialMedia"].includes(key) && typeof displayValue === "object" && displayValue !== null) {
              try {
                displayValue = JSON.stringify(displayValue, null, 2);
              } catch {
                displayValue = "[Invalid object]";
              }
            }
            return (
              <div style={{ display: "flex", alignItems: "center" }} key={key}>
                <TextField
                  label={`${key} (${source})`}
                  fullWidth
                  multiline={["keyPeople", "socialMedia"].includes(key)}
                  minRows={["keyPeople", "socialMedia"].includes(key) ? 3 : undefined}
                  disabled={readOnly || !isManual}
                  error={!!errors[key]}
                  value={displayValue}
                  onChange={e => {
                    let parsed = e.target.value;
                    if (["keyPeople", "socialMedia"].includes(key)) {
                      try { parsed = JSON.parse(parsed); } catch { /* ignore invalid */ }
                    }
                    handleResolvedDataFieldChange(key, parsed);
                  }}
                  sx={{ mb: 2 }}
                />
                <RestrictedAction allowed={!readOnly}>
                <IconButton
                  onClick={() => handleDeleteResolvedDataField(key)}
                  sx={{ marginLeft: 1, marginBottom: 2 }}
                  color="error"
                  aria-label="Delete"
                >
                  <CancelIcon />
                </IconButton>
                </RestrictedAction>
              </div>
            );
          })}
        </>
      )}

      {/* Add New Field */}
      {missingFields.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <Typography variant='h6'>Add a New Field</Typography>
          <Select
            value={newField}
            onChange={e => setNewField(e.target.value)}
            displayEmpty
            fullWidth
            disabled={readOnly}
            sx={{ mb: 2 }}
          >
            <MenuItem value='' disabled>
              Select a field
            </MenuItem>
            {missingFields.map(field => (
              <MenuItem key={field} value={field}>
                {field}
              </MenuItem>
            ))}
          </Select>
          {newField && (
            <TextField
              label={`Value for ${newField}`}
              fullWidth
              value={newFieldValue}
              error={!!errors[newField]}
              helperText={errors[newField]}
              onChange={e => setNewFieldValue(e.target.value)}
              disabled={readOnly}
              sx={{ mb: 2 }}
            />
          )}
          <RestrictedAction allowed={!readOnly}>
          <Button
            sx={{ width: 200, m: 2, alignSelf: "center" }}
            variant='contained'
            onClick={handleAddNewField}
            disabled={!newField || !newFieldValue}
          >
            Add Field
          </Button>
          </RestrictedAction>
        </div>
      )}

      {/* Add Custom Field */}
      <div style={{ marginBottom: "20px" }}>
        <Typography variant='h6'>Add a Custom Field</Typography>
        <TextField
          label='Custom Field Name'
          fullWidth
          value={customFieldName}
          error={!!errors.customFieldName}
          helperText={errors.customFieldName}
          onChange={e => setCustomFieldName(e.target.value)}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        <TextField
          label='Custom Field Value'
          fullWidth
          value={customFieldValue}
          onChange={e => setCustomFieldValue(e.target.value)}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        <RestrictedAction allowed={!readOnly}>
        <Button
          sx={{ width: 200, m: 2, alignSelf: "center" }}
          variant='contained'
          onClick={handleAddCustomField}
          disabled={!customFieldName || !customFieldValue}
        >
          Add Custom Field
        </Button>
        </RestrictedAction>
      </div>

      <RestrictedAction allowed={!readOnly}>
      <Button
        sx={{ width: 200, m: 2, alignSelf: "center" }}
        variant='contained'
        onClick={handleUpdateFinancialInfo}
        disabled={loading}
      >
        Update All Information
      </Button>
      </RestrictedAction>
    </div>
  );
};

export default function CustomSourcePage() {
  const { permissions } = useRolePermissions();
  const params = useParams();
  
  // Extract company ID from URL params as fallback
  const companyIdFromUrl = params.companyId || params.id;
  
  const [company, setCompany] = useState(null);

  const [additionalInfo, setAdditionalInfo] = useState("");
  const [aiSummaryText, setAiSummaryText] = useState("");
  const [textFieldValue, setTextFieldValue] = useState("");
  const [companySummary, setCompanySummary] = useState({});
  const [ticker, setTicker] = useState("");
  const [country, setCountry] = useState("");
  const [requestedCreditLimit, setRequestedCreditLimit] = useState(0);
  const [parentCompanyName, setParentCompanyName] = useState("");
  const [parentDnbCompanyId, setParentDnbCompanyId] = useState("");
  const [parentCountry, setParentCountry] = useState("");
  const [parentTicker, setParentTicker] = useState("");
  const [companyJsonData, setCompanyJsonData] = useState("");
  const [isManuallyAdded, setIsManuallyAdded] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [companyRecord, setCompanyRecord] = useState({});
  const [loading, setLoading] = useState(false);
  const [manualSource, setManualSource] = useState({
    equity: null,
    netIncome: null,
    revenue: null,
    numberOfEmployees: null,
    marketCap: null,
    yearsOfOperation: null,
    AQR: null,
  });
  const [avData, setAVData] = useState({});
  const [dnbData, setDnBData] = useState({});
  const [errors, setErrors] = useState({});
  const [isDnbCalled, setIsDnbCalled] = useState(false);
  const [isCreateCompanyModalOpen, setCreateCompanyModalOpen] = useState(false);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [additionalFile, setAdditionalFile] = useState(null);
  const [isUploadingAdditional, setIsUploadingAdditional] = useState(false);
  const additionalFileInputRef = useRef(null);
  const [alternativeData, setAlternativeData] = useState(null);
  const [isEditingAlternativeData, setIsEditingAlternativeData] = useState(false);
  const [editedAlternativeData, setEditedAlternativeData] = useState({});

  // Freeze automatic refresh: while `refreshFrozenUntil` (ISO) is in the future,
  // the underwriting service skips refreshes so manual edits are not overwritten.
  // Stored on the company summary (single source of truth).
  const [refreshFrozenUntil, setRefreshFrozenUntil] = useState(null);
  const [freezeDraft, setFreezeDraft] = useState(null);
  const [isSavingFreeze, setIsSavingFreeze] = useState(false);

  // Initialize freeze state whenever the company summary loads/changes.
  useEffect(() => {
    const frozenUntil = companySummary?.refreshFrozenUntil ?? null;
    setRefreshFrozenUntil(frozenUntil);
    setFreezeDraft(frozenUntil ? dayjs(frozenUntil) : null);
  }, [companySummary]);


  const isNumeric = (value) => !isNaN(parseFloat(value)) && isFinite(value);

  const validateFields = () => {
    let newErrors = {};
    const numericFields = ["assets", "operatingIncome", "revenue", "netIncome", "equity", "numberOfEmployees", "marketCap", "yearsOfOperation"];

    Object.keys(manualSource).forEach((key) => {
      if (key === 'AQR' && manualSource[key] && !isNumeric(manualSource[key])) {
        newErrors[key] = "AQR must be a valid ratio value";
      } else if (numericFields.includes(key)) {
        if (manualSource[key] && !isNumeric(manualSource[key])) {
          newErrors[key] = `${key} must be a numerical value`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchSummary = async () => {
    try {
      const companySummary = await underwritingApi.getCompanySummary(company?.tenantId, company?.id);
      setCompanySummary(companySummary);
      setAdditionalInfo(companySummary?.summaryData?.additionalInfo || "");
      setAiSummaryText(companySummary?.aiSummaryText || "");
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error("Error fetching company summary");
    }
  };

  // Freeze / unfreeze automatic refresh for this company.
  // Pass a dayjs date/time to freeze until, or null to clear the freeze.
  // Persisted on the company summary via updateCompanySummary (never triggers a refresh).
  const handleSaveFreeze = async (draftDate) => {
    if (!company?.id) return;
    setIsSavingFreeze(true);
    try {
      const isoValue = draftDate ? dayjs(draftDate).toISOString() : null;
      // Stored on the company summary (single source of truth). updateCompanySummary
      // does not trigger a refresh, so saving the freeze is safe.
      await underwritingApi.updateCompanySummary(company.tenantId, company.id, {
        refreshFrozenUntil: isoValue,
      });
      setRefreshFrozenUntil(isoValue);
      setFreezeDraft(isoValue ? dayjs(isoValue) : null);
      setCompanySummary((prev) => ({ ...(prev || {}), refreshFrozenUntil: isoValue }));
      toast.success(
        isoValue
          ? `Automatic refresh frozen until ${dayjs(isoValue).format("MMM D, YYYY h:mm A")}`
          : "Automatic refresh re-enabled"
      );
    } catch (error) {
      console.error("Error updating refresh freeze:", error);
      toast.error("Failed to update refresh freeze");
    } finally {
      setIsSavingFreeze(false);
    }
  };

  // 1. Custom Text - updateCompany in companyApi with field customSource
  const handleUpdateCustomSourceClick = async () => {
    setLoading(true);
    try {
      const updateData = {
        tenantId: company.tenantId,
        id: company.id,
        customSource: textFieldValue,
      };
      
      await companyApi.updateCompany(company.id, updateData);
      toast.success("Custom text updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating custom text");
    }
    setLoading(false);
  };

  // 2. Additional Info - updateCompanySummary in underwritingApi with additionalInfo field in summaryData object
  const handleUpdateAddInfo = async () => {
    try {
      const summaryData = {
        ...companySummary?.summaryData,
        additionalInfo: additionalInfo
      };
      
      await underwritingApi.updateCompanySummary(company.tenantId, company.id, {
        summaryData
      });
      
      toast.success("Additional information updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating additional info");
    }
  };

  // 3. Summary - updateCompanySummary in underwritingApi with aiSummary field in summaryData object
  const handleUpdateAiSummary = async () => {
    try {

      const updatedCompanySummary = {
        ...companySummary,
        aiSummaryText: aiSummaryText
      };

      await underwritingApi.updateCompanySummary(company.tenantId, company.id, updatedCompanySummary);
      toast.success("Summary updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating summary");
    }
  };

  // 4. Update CompanyInfo - updateCompany in companyAPi with respective fields (Remove Wikiinfobox)
  const handleUpdateCompanyInfo = async () => {
    setLoading(true);
    try {
      const updateData = {
        tenantId: company.tenantId,
        id: company.id,
        ticker,
        country,
        requestedCreditLimit,
        parentCompanyName,
        parentDnbCompanyId,
        parentCountry,
        parentTicker,
        companyJsonData,
        isManuallyAdded,
      };
      
      await companyApi.updateCompany(company.id, updateData);
      toast.success("Company information updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating company information");
    }
    setLoading(false);
  };

  // 5. Financial Information - Get data from aggregated data API
  const fetchFinancialData = async () => {
    try {
      const aggregatedData = await underwritingApi.getAggregatedData(company.id);
      
      // Alpha Vantage data
      if (aggregatedData?.standardizedData?.alpha_vantage) {
        setAVData(aggregatedData.standardizedData.alpha_vantage);
      }
      
      // DnB data
      if (aggregatedData?.standardizedData?.dnb) {
        setDnBData(aggregatedData.standardizedData.dnb);
      }

      // Fetch Alternative Data
      try {
        const altResponse = await underwritingApi.getAlternativeData(company.id);
        if (altResponse && altResponse.data) {
          console.log("Fetched Alternative Data:", altResponse.data);
          setAlternativeData(altResponse.data);
        }
      } catch (err) {
        console.log("No alternative data found or error fetching", err);
      }
    } catch (error) {
      console.error("Error fetching financial data:", error);
    }
  };

  // Manual Source - Get Data from Company Object and update
  const fetchManualSource = async () => {
    try {
      const response = await companyApi.getCompanyById(company.id);
      const companyData = response?.data || response;

      if (companyData?.manualSource) {
        let parsedManualSource;
        if (typeof companyData.manualSource === "string") {
          try {
            parsedManualSource = JSON.parse(companyData.manualSource);
          } catch (e) {
            console.error("Failed to parse manualSource string:", e);
            parsedManualSource = {
              equity: null,
              netIncome: null,
              revenue: null,
              numberOfEmployees: null,
              marketCap: null,
              yearsOfOperation: null,
              AQR: null,
            };
          }
        } else if (typeof companyData.manualSource === "object" && companyData.manualSource !== null) {
          parsedManualSource = companyData.manualSource;
        } else {
          parsedManualSource = {
            equity: null,
            netIncome: null,
            revenue: null,
            numberOfEmployees: null,
            marketCap: null,
            yearsOfOperation: null,
            AQR: null,
          };
        }
        setManualSource(parsedManualSource);
      }
    } catch (error) {
      console.error("Error fetching manual source:", error);
    }
  };

  const handleManualSourceChange = (key, value) => {
    console.log("key, value", key, value);
    setManualSource((prevManualSource) => ({
      ...prevManualSource,
      [key]: value,
    }));
  };

  const getCurrentTenant = async () => {
    const selectedTenant = getTenantIdFromURL();
    const isAdmin = await isAdminAuth0();
    const tenantId = isAdmin ? selectedTenant : await getTenantIdAuth0();
    console.log("getCurrentTenant");
    
    try {
      const response = await tenantApi.getTenantById(tenantId);
      console.log("Current Tenant -> ", response?.data);
      setCurrentTenant(response?.data);
    } catch (error) {
      console.error("Error fetching tenant:", error);
    }
  };

  const setCallDnbManuallyFlag = async () => {
    try {
      setLoading(true);
      console.log("Setting callDnbManuallyFlag true", company.tenantId);
      const companySummaryRecord = await underwritingApi.getCompanySummary(company?.tenantId, company?.id);
      const companySummary = companySummaryRecord?.summaryData;
      if (companySummary) {
        console.log("Updating flag for company", company.name);
        companySummary.callDnbManuallyFlag = true;
        
        await underwritingApi.updateCompanySummary(company.tenantId, company.id, {
          summaryData: JSON.stringify(companySummary)
        });
        
        // Trigger underwriting flow using new API
        await companyApi.mockUnderwritingFlow(company.tenantId, company.id);
        toast.success("Underwriting Triggered with manual DnB call!");
        console.log("Flag updated successfully");
      } else {
        console.log("Company not found in summary", company.name);
      }
    } catch (error) {
      console.error("Error setting callDnbManuallyFlag", error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (number) => {
    if (isNaN(number) || !number) {
      return "-"; // Return a placeholder for missing values
    }
    if (number >= 1e9) {
      return `US $${(number / 1e9).toFixed(2)} billion`;
    } else if (number >= 1e6) {
      return `US $${(number / 1e6).toFixed(2)} million`;
    } else if (number >= 1e3) {
      return `US $${(number / 1e3).toFixed(2)} thousand`;
    } else {
      return `US $${number}`;
    }
  };

  const getFieldValue = (fieldData) => {
    // Check if fieldData has a value property (new structure)
    if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
      return fieldData.value;
    }
    // Otherwise, treat fieldData as the direct value (old structure)
    return fieldData;
  };

  const handleUpdateAlternativeData = async () => {
    try {
      setLoading(true);
      
      const processedEditedData = { ...editedAlternativeData };
      // No need to re-attach hidden JSON, we are editing the full content now

      // Merge edited fields into current alternative data
      const updatedData = {
        ...alternativeData,
        ...processedEditedData
      };
      
      const response = await underwritingApi.updateAlternativeData(company.id, updatedData);
      
      if (response && response.success) {
        setAlternativeData(response.data?.data || updatedData);
        setIsEditingAlternativeData(false);
        setEditedAlternativeData({});
        toast.success("Alternative Data updated successfully!");
      } else {
        toast.error("Failed to update Alternative Data");
      }
    } catch (error) {
      console.error("Error updating alternative data:", error);
      toast.error("Error updating Alternative Data");
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditingAlternativeData = () => {
      // Prepare for editing
      const initialEdits = {};
      
      // Special handling for report: Convert legacy HTML if needed
      if (alternativeData.report) {
          let reportText = alternativeData.report;

          // 2. Convert Legacy HTML to Markdown
          if (reportText.trim().startsWith('<') && reportText.includes('>')) {
              try {
                 const turndownService = new TurndownService({ 
                    codeBlockStyle: 'fenced',
                    headingStyle: 'atx'
                 });
                 turndownService.use(gfm);
                 reportText = turndownService.turndown(reportText);
                 console.log("Auto-converted legacy HTML to Markdown");
              } catch (e) {
                 console.warn("Auto-conversion of HTML to MD failed", e);
              }
          }
          
          initialEdits.report = reportText;
      }
      
      setEditedAlternativeData(initialEdits);
      setIsEditingAlternativeData(true);
  };

  // Toolbar assistance
  const reportInputRef = useRef(null);

  const handleInsertMarkdown = (type) => {
      const textarea = reportInputRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editedAlternativeData.report || "";
      const selected = text.substring(start, end);
      
      let insertion = "";
      let cursorOffset = 0;

      switch (type) {
          case 'bold':
              insertion = `**${selected || 'bold text'}**`;
              cursorOffset = selected ? 0 : -2; // Move cursor inside if placeholder
              break;
          case 'italic':
              insertion = `*${selected || 'italic text'}*`;
              cursorOffset = selected ? 0 : -1;
              break;
          case 'strikethrough':
              insertion = `~~${selected || 'text'}~~`;
              cursorOffset = selected ? 0 : -2;
              break;
          case 'h1':
              insertion = `\n# ${selected || 'Heading 1'}\n`;
              break;
          case 'h2':
               insertion = `\n## ${selected || 'Heading 2'}\n`;
               break;
          case 'h3':
               insertion = `\n### ${selected || 'Heading 3'}\n`;
               break;
          case 'list':
               insertion = `\n- ${selected || 'List item'}`;
               break;
          case 'numbered-list':
               insertion = `\n1. ${selected || 'List item'}`;
               break;
          case 'quote':
               insertion = `\n> ${selected || 'Blockquote'}\n`;
               break;
          case 'code':
               if (selected.includes('\n')) {
                   insertion = `\n\`\`\`\n${selected}\n\`\`\`\n`;
               } else {
                   insertion = `\`${selected || 'code'}\``;
               }
               break;
          case 'link':
               insertion = `[${selected || 'link text'}](url)`;
               break;
          case 'image':
               insertion = `![${selected || 'alt text'}](image_url)`;
               break;
          case 'rule':
               insertion = `\n---\n`;
               break;
          case 'table':
              insertion = `\n| Column 1 | Column 2 | Column 3 |\n|---|---|---|\n| Row 1 | Data | Data |\n| Row 2 | Data | Data |\n`;
              break;
          default:
              return;
      }

      const newText = text.substring(0, start) + insertion + text.substring(end);
      
      setEditedAlternativeData(prev => ({ ...prev, report: newText }));
      
      // Need to defer focus/selection setting to after render
      setTimeout(() => {
          textarea.focus();
          // textarea.setSelectionRange(...) - simplified for now
      }, 0);
  };

  const handleDunsIdUpdate = async (dunsId) => {
    console.log("Duns Id ", dunsId);
    setCreateCompanyModalOpen(false);

    try {
      const updateData = {
        tenantId: company.tenantId,
        id: company.id,
        dnbCompanyId: dunsId, 
      };
      
      await companyApi.updateCompany(company.id, updateData);
      console.log("Company updated with new DUNS ID");
      toast.success("DnB ID updated successfully!");
      await setCallDnbManuallyFlag();
    } catch (error) {
      console.error("Error updating DUNS ID:", error);
      toast.error("Failed to update DUNS ID.");
    }
  };

  const fetchAdditionalFiles = async () => {
    try {
      if (company?.id) {
        const response = await underwritingApi.getAdditionalFiles(company.id);
        if (response && response.data) {
          setAdditionalFiles(Array.isArray(response.data) ? response.data : response.data.files || []);
        }
      }
    } catch (error) {
      console.error('Error fetching additional files:', error);
    }
  };

  const handleAdditionalFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setAdditionalFile(selectedFile);
    }
  };

  const handleAdditionalFileButtonClick = () => {
    if (additionalFileInputRef.current) {
      additionalFileInputRef.current.click();
    }
  };

  const uploadAdditionalFile = async () => {
    if (!additionalFile || !company?.id) return;

    setIsUploadingAdditional(true);

    try {
      // Step 1: Get signed URL for direct upload
      const signedUrlResponse = await companyApi.getAdditionalFileUploadSignedUrl(
        company.id,
        {
          fileName: additionalFile.name,
          fileType: additionalFile.type,
          companyId: company.id,
          tenantId: company.tenantId
        }
      );

      // Extract data from response (response structure: { success, message, data: { uploadUrl, fileGCSKey, fileName } })
      const responseData = signedUrlResponse?.data || signedUrlResponse;
      
      if (!responseData || !responseData.uploadUrl) {
        throw new Error(signedUrlResponse?.message || 'Failed to get upload URL');
      }

      // Step 2: Upload file directly to Google Cloud Storage
      await axios.put(responseData.uploadUrl, additionalFile, {
        headers: {
          'Content-Type': additionalFile.type,
        },
      });

      // Step 3: Create additional file record
      const fileData = {
        tenantId: company.tenantId,
        companyId: company.id,
        fileGCSKey: responseData.fileGCSKey,
        fileName: additionalFile.name,
        fileSize: additionalFile.size,
        fileType: additionalFile.type
      };

      const createResponse = await underwritingApi.createAdditionalFile(company.id, fileData);

      if (createResponse && createResponse.data) {
        setAdditionalFiles(prevFiles => [createResponse.data, ...prevFiles]);
        toast.success('Additional file uploaded successfully!');
        setAdditionalFile(null);
        // Reset file input
        if (additionalFileInputRef.current) {
          additionalFileInputRef.current.value = '';
        }
      } else {
        throw new Error('Failed to create file record');
      }

    } catch (error) {
      console.error('Error uploading additional file:', error);
      toast.error('Failed to upload additional file');
    } finally {
      setIsUploadingAdditional(false);
    }
  };

  const handleDeleteAdditionalFile = async (fileToDelete) => {
    try {
      await underwritingApi.deleteAdditionalFile(fileToDelete.id);
      setAdditionalFiles(prevFiles => prevFiles.filter(f => f.id !== fileToDelete.id));
      toast.success('Additional file removed successfully');
    } catch (error) {
      console.error('Error deleting additional file:', error);
      toast.error('Failed to remove additional file');
    }
  };

  const handleAdditionalFileDownload = async (fileToDownload) => {
    try {
      const response = await companyApi.getAdditionalFileDownloadUrl(fileToDownload.id);
      if (response && response.data && response.data.downloadUrl) {
        // Fetch the file as a blob
        const fileResponse = await fetch(response.data.downloadUrl);
        const blob = await fileResponse.blob();

        // Create a blob URL and force download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileToDownload.fileName || 'download');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Download started');
      } else {
        throw new Error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  useEffect(() => {
    (async () => {
      // Fetch company data first
      if (companyIdFromUrl) {
        try {
          const companyData = await companyApi.getCompanyById(companyIdFromUrl);
          setCompany(companyData?.data);
          
          // Initialize state with company data
          setTextFieldValue(companyData.customSource || "");
          setTicker(companyData.ticker || "");
          setCountry(companyData.country || "");
          setRequestedCreditLimit(companyData.requestedCreditLimit || 0);
          setParentCompanyName(companyData.parentCompanyName || "");
          setParentDnbCompanyId(companyData.parentDnbCompanyId || "");
          setParentCountry(companyData.parentCountry || "");
          setParentTicker(companyData.parentTicker || "");
          setCompanyJsonData(companyData.companyJsonData || "");
          setIsManuallyAdded(companyData.isManuallyAdded || false);
          
          // Initialize manualSource
          setManualSource(companyData?.manualSource ? companyData.manualSource : {
            equity: null,
            netIncome: null,
            revenue: null,
            numberOfEmployees: null,
            marketCap: null,
            yearsOfOperation: null,
            AQR: null,
          });
        } catch (error) {
          console.error("Error fetching company:", error);
          toast.error("Failed to load company data");
        }
      }
      
      getCurrentTenant();
    })();
  }, [companyIdFromUrl]);

  // Separate useEffect for functions that depend on company being loaded
  useEffect(() => {
    if (company) {
      (async () => {
        await fetchSummary();
        await fetchFinancialData();
        await fetchManualSource();
        await fetchAdditionalFiles();
      })();
    }
  }, [company]);

  if (!company) {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      <Paper
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          padding: "10px",
        }}
      >
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
          }}
        >
          <Typography variant="h2" fontWeight="bold" color="text.primary">
            {company.name} {companySummary?.resolvedData?.industry?.value ? `(${companySummary?.resolvedData?.industry.value})`: ""}
          </Typography>
        </Box>

        {/* Refresh freeze removed platform-wide — a refresh always updates the data. */}

        {/* 1. Custom Text */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Custom Text
        </Typography>
        <ReactQuill
          value={textFieldValue}
          onChange={(value) => setTextFieldValue(value)}
          modules={{
            toolbar: [
              ["bold", "italic", "underline", "strike"],
              [{ align: [] }],
            ],
          }}
          formats={["bold", "italic", "underline", "strike", "align"]}
          placeholder='Enter custom text'
          className='custom-quill'
        />
        <Button
          sx={{ width: 200, m: 2, alignSelf: "center" }}
          variant="contained"
          onClick={handleUpdateCustomSourceClick}
          disabled={loading}
        >
          Update Custom Source
        </Button>
        
        {/* 2. Additional Info */}
        <div className="div">
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ alignSelf: "flex-start", mb: 2 }}
          >
            Additional Info
          </Typography>
          <ReactQuill
            value={additionalInfo}
            onChange={(value) => setAdditionalInfo(value)}
            modules={{
              toolbar: [
                ["bold", "italic", "underline", "strike"],
                [{ align: [] }],
              ],
            }}
            formats={["bold", "italic", "underline", "strike", "align"]}
            theme='snow'
            placeholder='Enter custom text'
            className='custom-quill'
          />
          <Button
            sx={{ width: 200, m: 2, alignSelf: "center" }}
            variant="contained"
            onClick={handleUpdateAddInfo}
            disabled={loading}
          >
            Update Additional Info
          </Button>
        </div>
        
        {/* 3. Summary */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Summary
        </Typography>
        <TextField
          id="outlined-multiline-flexible"
          label="Enter AI Summary"
          multiline
          fullWidth
          value={aiSummaryText}
          onChange={(e) => setAiSummaryText(e.target.value)}
        />
        <Button
          sx={{ width: 200, m: 2, alignSelf: "center" }}
          variant="contained"
          onClick={handleUpdateAiSummary}
          disabled={loading}
        >
          Update Summary
        </Button>
        
        {/* 4. Company Info Fields */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Country
        </Typography>
        <TextField
          id="country"
          label="Country"
          fullWidth
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        />
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Ticker
        </Typography>
        <TextField
          id="ticker"
          label="Ticker"
          fullWidth
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
        />
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Requested Credit Limit
        </Typography>
        <TextField
          id="requestedCreditLimit"
          label="Requested Credit Limit"
          type="number"
          fullWidth
          value={requestedCreditLimit}
          onChange={(e) => setRequestedCreditLimit(parseFloat(e.target.value))}
        />
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Parent Company Name
        </Typography>
        <TextField
          id="parentCompanyName"
          label="Parent Company Name"
          fullWidth
          value={parentCompanyName}
          onChange={(e) => setParentCompanyName(e.target.value)}
        />
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
        
          Parent DNB Company ID
        </Typography>
        <TextField
          id="parentDnbCompanyId"
          label="Parent DNB Company ID"
          fullWidth
          value={parentDnbCompanyId}
          onChange={(e) => setParentDnbCompanyId(e.target.value)}
        />
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Parent Country
        </Typography>
        <TextField
          id="parentCountry"
          label="Parent Country"
          fullWidth
          value={parentCountry}
          onChange={(e) => setParentCountry(e.target.value)}
        />
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          Parent Ticker
        </Typography>
        <TextField
          id="parentTicker"
          label="Parent Ticker"
          fullWidth
          value={parentTicker}
          onChange={(e) => setParentTicker(e.target.value)}
        />
        
        <Button
          sx={{ width: 200, m: 2, alignSelf: "center" }}
          variant="contained"
          onClick={handleUpdateCompanyInfo}
          disabled={loading}
        >
          Update Company Info
        </Button>
        
        {/* 5. Financial Information */}
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", my: 2 }}
        >
          Financial Information
        </Typography>
        
        {/* Alpha Vantage Data */}
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", my: 2 }}
        >
          Alpha Vantage:
        </Typography>
        {avData && Object.keys(avData).length > 0 ? (
          Object.keys(avData).map((key) => {
            const fieldValue = getFieldValue(avData[key]);
            return (
              <Typography key={key} sx={{ mb: 2 }}>
                <strong>{key}:</strong> {formatNumber(fieldValue)}
              </Typography>
            );
          })
        ) : (
          "No Alpha Vantage Data Available"
        )}
        
        {/* DnB Data */}
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ alignSelf: "flex-start", my: 2 }}
        >
          DnB:
        </Typography>
        {dnbData && Object.keys(dnbData).length > 0 ? (
          Object.keys(dnbData).map((key) => (
            <Typography key={key} sx={{ mb: 2 }}>
              <strong>{key}:</strong> {dnbData[key]}
            </Typography>
          ))
        ) : (
          <>
            <Typography>No DnB Data Available</Typography>
            <RestrictedAction allowed={permissions.canEditEntity}>
            <Button
              variant='contained'
              onClick={setCallDnbManuallyFlag}
              disabled={(!company?.dnbCompanyId)|| (!dnbData && isDnbCalled) || loading}
              sx={{ width: 200, alignSelf: 'left' }}
            >
              Call DnB Manually
            </Button>
            </RestrictedAction>
            {!company?.dnbCompanyId && (
              <RestrictedAction allowed={permissions.canCreateEntity}>
              <Button
                variant="contained"
                onClick={() => setCreateCompanyModalOpen(true)}
                sx={{ width: 200, alignSelf: 'left', mt:2 }}
              >
                Search DnB
              </Button>
              </RestrictedAction>
            )}
            {!dnbData && isDnbCalled && (
              <Typography sx={{ color: "gray" }}>
                DnB API already called.
              </Typography>
            )}
          </>
        )}
        
        {/* Manual Source */}
        <ManualSourceForm
          manualSource={manualSource}
          handleManualSourceChange={handleManualSourceChange}
          loading={loading}
          errors={errors}
          setErrors={setErrors}
          setManualSource={setManualSource}
          companyId={company.id}
          company={company}
          setLoading={setLoading}
          validateFields={validateFields}
          companySummary={companySummary}  // Pass companySummary
          setCompanySummary={setCompanySummary} // Pass setCompanySummary
          readOnly={!permissions.canEditEntity}
        />

        {/* Alternative Data Section */}
        {alternativeData && (
          <Paper
            elevation={2}
            sx={{
              padding: '25px',
              background: '#FAFAFC',
              borderRadius: '8px',
              marginTop: '20px'
            }}
          >
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                  style={{
                    fontSize: "20px",
                    fontWeight: "500",
                    color: "#1A2A56",
                  }}
                >
                  Alternative Data (AI Generated)
                </Typography>
                {!isEditingAlternativeData ? (
                  <RestrictedAction allowed={permissions.canEditEntity}>
                  <Button variant="outlined" onClick={handleStartEditingAlternativeData}>
                    Edit
                  </Button>
                  </RestrictedAction>
                ) : (
                  <Box>
                    <Button variant="outlined" color="error" onClick={() => {
                        setIsEditingAlternativeData(false);
                        setEditedAlternativeData({});
                      }} sx={{ mr: 1 }}>
                      Cancel
                    </Button>
                    <RestrictedAction allowed={permissions.canEditEntity}>
                    <Button variant="contained" onClick={handleUpdateAlternativeData} disabled={loading}>
                      Save
                    </Button>
                    </RestrictedAction>
                  </Box>
                )}
             </Box>
             
             {/* Display/Edit Loop */}
             {Object.entries(alternativeData).map(([key, value]) => {
                // Skip internal fields
                if (['id', 'tenantId', 'createdAt', 'updatedAt', 'success', 'message', 'status', 'lastUpdated', 'companyId', 'estimatedTokens'].includes(key)) return null;

                const isReportField = key === 'report';
                
                // For report field loops:
                // View Mode: Display full value (including JSON table)
                // Edit Mode: Display only the edited text (without JSON)
                const displayValue = isEditingAlternativeData 
                                    ? (editedAlternativeData[key] !== undefined ? editedAlternativeData[key] : value)
                                    : value;
                
                return (
                   <Box key={key} sx={{ mb: 2 }}>
                      {isEditingAlternativeData ? (
                         isReportField ? (
                           <React.Fragment>
                             <Typography variant="subtitle2" color="primary" sx={{mb:1}}>
                               Edit Report (Markdown Supported)
                             </Typography>
                             <Typography variant="caption" sx={{mb:1, display: 'block', color: 'text.secondary'}}>
                               Use the tabs to switch between <strong>Write</strong> and <strong>Preview</strong> mode.
                             </Typography>

                             <div className="container" style={{ marginBottom: '20px' }}>
                                <MDEditor
                                    value={editedAlternativeData[key] || ''}
                                    onChange={(value) => setEditedAlternativeData(prev => ({ ...prev, [key]: value || '' }))}
                                    height={500}
                                />
                             </div>


                           </React.Fragment>
                         ) : (
                           <TextField
                              label={key}
                              fullWidth
                              value={displayValue || ''}
                              onChange={(e) => setEditedAlternativeData(prev => ({ ...prev, [key]: e.target.value }))}
                              multiline={String(displayValue).length > 50}
                           />
                         )
                      ) : (
                         <React.Fragment>
                            {/* <Typography sx={{ fontWeight: 'bold' }}>{key}:</Typography>  */}
                            {isReportField ? (
                              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <ReportViewer markdownText={String(displayValue)} tenantId={company?.tenantId} />
                              </Box>
                            ) : (
                              <Typography>{String(displayValue)}</Typography>
                            )}
                         </React.Fragment>
                      )}
                   </Box>
                );
             })}
          </Paper>
        )}
        
        {/* Additional Files Section */}
        <Paper
          elevation={2}
          sx={{
            padding: '25px',
            background: '#FAFAFC',
            borderRadius: '8px',
            marginTop: '20px'
          }}
        >
          <Typography
            style={{
              fontSize: "20px",
              fontWeight: "500",
              color: "#1A2A56",
              marginBottom: '20px'
            }}
          >
            Additional Files
          </Typography>

          <RestrictedRegion allowed={permissions.canUploadDocuments}>
          <Box
            style={{
              height: '180px',
              background: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              padding: '25px',
              marginBottom: '20px'
            }}
          >
            <Typography
              onClick={handleAdditionalFileButtonClick}
              style={{
                paddingTop: '10px',
                textTransform: 'none',
                color: "#5186EC",
                fontSize: '16px',
                fontWeight: '200',
                textDecoration: 'underline',
                cursor: 'pointer',
                marginBottom: '20px'
              }}
            >
              Select a file to upload!
            </Typography>
            <input
              type="file"
              ref={additionalFileInputRef}
              onChange={handleAdditionalFileChange}
              style={{ display: 'none' }}
              accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
            />
            <div style={{ display: "flex", flexDirection: "row", borderBottom: '1px solid  #777A82', width: '100%', paddingBottom: '10px', paddingTop: '10px' }}>
              {additionalFile === null && (
                <Typography style={{
                  width: '264px',
                  fontSize: '14px',
                  fontWeight: '200',
                  color: '#777A82',
                }}>
                  No file selected!
                </Typography>
              )}
              {additionalFile && (
                <Typography style={{
                  width: '264px',
                  fontSize: '14px',
                  fontWeight: '200',
                  color: '#777A82',
                }}>
                  {additionalFile.name}
                </Typography>
              )}
              <Button
                onClick={uploadAdditionalFile}
                disabled={isUploadingAdditional || !additionalFile}
                style={{
                  width: '87px',
                  height: '30px',
                  background: '#5186EC 0% 0% no-repeat padding-box',
                  borderRadius: '8px',
                  opacity: 1,
                  color: 'white',
                  fontWeight: '200',
                  textTransform: 'none',
                }}
              >
                {isUploadingAdditional ? 'Uploading...' : 'Add File'}
              </Button>
            </div>
          </Box>
          </RestrictedRegion>

          {additionalFiles && additionalFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#1A2A56",
                  marginBottom: '15px'
                }}
              >
                Uploaded Additional Files!
              </Typography>
              <List>
                {additionalFiles.map((file, index) => (
                  <ListItem
                    key={file.id || index}
                    sx={{
                      border: '1px solid #E0E0E0',
                      borderRadius: '4px',
                      mb: 1,
                      background: '#FFFFFF'
                    }}
                  >
                    <ListItemText
                      primary={file.fileName}
                      style={{
                        color: '#777A82'
                      }}
                    />
                    <RestrictedAction allowed={permissions.canDownloadReports}>
                    <Button
                      style={{
                        minWidth: "30px",
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center",
                        padding: "4px",
                        marginLeft: "10px"
                      }}
                      onClick={() => handleAdditionalFileDownload(file)}
                    >
                      <DownloadIcon sx={{ height: '20px', color: '#777A82' }} />
                    </Button>
                    </RestrictedAction>
                    <RestrictedAction allowed={permissions.canUploadDocuments}>
                    <Button
                      style={{
                        minWidth: "30px",
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center",
                        padding: "4px",
                        marginLeft: "10px"
                      }}
                      onClick={() => handleDeleteAdditionalFile(file)}
                    >
                      <DeleteIcon sx={{ height: '20px', color: '#777A82' }} />
                    </Button>
                    </RestrictedAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
        
        <CreateCompanyModal
          open={isCreateCompanyModalOpen}
          onClose={() => setCreateCompanyModalOpen(false)}
          onDunsIdSelect={handleDunsIdUpdate}
        />
      </Paper>
    </React.Fragment>
  );
}
